import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Upload, LogOut, ExternalLink } from "lucide-react";
import { api, fetchContent, formatApiError, imgSrc } from "@/lib/api";
import { BookingsTab, CalendarTab } from "@/pages/AdminBookings";

const TABS = ["Settings", "Hero", "Villa", "Rooms", "Gallery", "Experiences", "FAQ", "Reviews", "SEO", "Blog", "Bookings", "Calendar"];

const inputCls = "mt-1 w-full rounded-lg border border-white/10 bg-charcoal-800 px-3 py-2.5 text-sm text-cream focus:border-copper focus:outline-none";

function Text({ label, value, onChange, textarea, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400">{label}</span>
      {textarea ? (
        <textarea rows={rows} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls} />
      ) : (
        <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls} />
      )}
    </label>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400">{label}</span>
      <div className="mt-1 flex items-center gap-3">
        {value && <img src={imgSrc(value)} alt="" className="h-14 w-20 rounded-md border border-white/10 object-cover" />}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls} placeholder="Image URL or upload →" />
        <label className={`flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-copper/40 px-3 py-2 text-xs text-copper-300 hover:bg-copper/10 ${busy ? "opacity-50" : ""}`} data-testid={`${testid}-upload`}>
          <Upload className="h-3.5 w-3.5" /> {busy ? "…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>
    </div>
  );
}

function Card({ children, onDelete }) {
  return (
    <div className="relative space-y-3 rounded-xl border border-white/10 bg-charcoal-800/60 p-4">
      {onDelete && (
        <button onClick={onDelete} className="absolute right-3 top-3 text-cream-dim hover:text-red-400" aria-label="Delete item">
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {children}
    </div>
  );
}

const addBtnCls = "inline-flex items-center gap-1.5 rounded-full border border-copper/40 px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] text-copper-300 hover:bg-copper/10";

export default function AdminDashboard() {
  const [user, setUser] = useState(undefined);
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("Settings");
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);
  useEffect(() => {
    if (user) {
      fetchContent().then(setContent);
      api.get("/admin/blog").then((r) => setPosts(r.data));
    }
  }, [user]);

  if (user === null) return <Navigate to="/admin/login" replace />;
  if (!user || !content) return <main className="flex min-h-screen items-center justify-center text-cream-muted">Loading…</main>;

  const update = (fn) => {
    const c = structuredClone(content);
    fn(c);
    setContent(c);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Saved — live on the site now");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login");
  };

  const s = content.settings;
  const setArr = (key) => (fn) => update((c) => { const arr = [...(c[key] || [])]; fn(arr); c[key] = arr; });

  const savePost = async () => {
    try {
      if (editing.id) await api.put(`/admin/blog/${editing.id}`, editing);
      else await api.post("/admin/blog", editing);
      const { data } = await api.get("/admin/blog");
      setPosts(data);
      setEditing(null);
      toast.success("Post saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <main className="min-h-screen bg-charcoal pb-24" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-8">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2.5 font-serif text-lg text-cream"><img src="/logo.png" alt="" className="h-9 w-9 rounded-full border border-copper/40 object-cover" />Mystique <span className="text-copper-gradient italic">Copper</span> Admin</span>
            <Link to="/" className="flex items-center gap-1 text-xs text-cream-dim hover:text-copper-300" data-testid="admin-view-site-link"><ExternalLink className="h-3 w-3" /> View site</Link>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="admin-save-settings-btn"
              className="rounded-full bg-copper px-6 py-2.5 text-xs font-mono uppercase tracking-[0.15em] text-charcoal hover:bg-copper-300 disabled:opacity-60">
              {saving ? "Saving…" : "Save All"}
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 text-xs text-cream-dim hover:text-copper-300">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 pb-2 sm:px-8" role="tablist">
          {TABS.map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs transition-colors ${tab === t ? "bg-copper text-charcoal" : "text-cream-muted hover:text-copper-300"}`}>
              {t}
            </button>
          ))}
        </div>
      </header>

      <div className="mx-auto max-w-6xl space-y-4 px-4 py-8 sm:px-8">
        {tab === "Settings" && (
          <>
            <p className="text-sm text-cream-muted">Paste your Asiatech booking engine URL and contact details here. Empty fields stay hidden on the public site.</p>
            <Text label="Asiatech booking engine URL" testid="admin-booking-url-input" value={s.booking_engine_url} onChange={(v) => update((c) => (c.settings.booking_engine_url = v))} placeholder="https://booking.asiatech.in/… (check-in/out & guests are appended automatically)" />
            <div className="grid gap-4 sm:grid-cols-2">
              <Text label="WhatsApp number (with country code)" testid="admin-whatsapp-input" value={s.whatsapp_number} onChange={(v) => update((c) => (c.settings.whatsapp_number = v))} placeholder="919876543210" />
              <Text label="Phone" testid="admin-phone-input" value={s.phone} onChange={(v) => update((c) => (c.settings.phone = v))} placeholder="+91 …" />
              <Text label="Email" value={s.email} onChange={(v) => update((c) => (c.settings.email = v))} />
              <Text label="Address" value={s.address} onChange={(v) => update((c) => (c.settings.address = v))} />
              <Text label="Google Maps embed URL" value={s.maps_embed_url} onChange={(v) => update((c) => (c.settings.maps_embed_url = v))} />
              <Text label="Google Maps directions link" value={s.maps_directions_url} onChange={(v) => update((c) => (c.settings.maps_directions_url = v))} />
              <Text label="Google reviews link" value={s.google_reviews_url} onChange={(v) => update((c) => (c.settings.google_reviews_url = v))} />
              <Text label="Instagram URL" value={s.instagram_url} onChange={(v) => update((c) => (c.settings.instagram_url = v))} />
              <Text label="GA4 measurement ID" value={s.ga4_id} onChange={(v) => update((c) => (c.settings.ga4_id = v))} placeholder="G-XXXXXXX" />
              <Text label="Meta Pixel ID" value={s.meta_pixel_id} onChange={(v) => update((c) => (c.settings.meta_pixel_id = v))} />
            </div>
            <div className="mt-8 space-y-4 rounded-xl border border-copper/25 bg-copper/5 p-5">
              <p className="font-serif text-lg text-cream">Payments (UPI / GPay)</p>
              <p className="text-sm text-cream-muted">Guests pay the advance by scanning your QR at checkout. Set the nightly tariff to show totals automatically.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Text label="UPI ID (VPA)" testid="admin-upi-id-input" value={s.upi_id} onChange={(v) => update((c) => (c.settings.upi_id = v))} placeholder="yourname@okhdfcbank" />
                <Text label="UPI payee name" value={s.upi_name} onChange={(v) => update((c) => (c.settings.upi_name = v))} placeholder="Mystique Copper Villa" />
                <Text label="Price per night ₹ (up to base guests)" testid="admin-price-input" value={s.base_price} onChange={(v) => update((c) => (c.settings.base_price = v))} placeholder="e.g. 5500" />
                <Text label="Base guests included" value={s.base_guests} onChange={(v) => update((c) => (c.settings.base_guests = v))} placeholder="e.g. 4" />
                <Text label="Extra guest ₹ / person / night" testid="admin-extra-guest-input" value={s.extra_guest_price} onChange={(v) => update((c) => (c.settings.extra_guest_price = v))} placeholder="e.g. 850" />
                <Text label="Advance % (blank = full amount)" value={s.advance_percent} onChange={(v) => update((c) => (c.settings.advance_percent = v))} placeholder="e.g. 50" />
              </div>
              <div className="space-y-3">
                <p className="text-sm text-cream">Seasonal tariff <span className="text-cream-dim">— % increase applied to nights in the range</span></p>
                {(s.seasons || []).map((sea, i) => (
                  <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                    <label className="block"><span className="text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400">Name</span>
                      <input value={sea.name || ""} onChange={(e) => update((c) => (c.settings.seasons[i].name = e.target.value))} placeholder="e.g. New Year peak" className={inputCls} /></label>
                    <label className="block"><span className="text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400">From</span>
                      <input type="date" value={sea.from || ""} onChange={(e) => update((c) => (c.settings.seasons[i].from = e.target.value))} className={`${inputCls} [color-scheme:dark]`} /></label>
                    <label className="block"><span className="text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400">To</span>
                      <input type="date" value={sea.to || ""} onChange={(e) => update((c) => (c.settings.seasons[i].to = e.target.value))} className={`${inputCls} [color-scheme:dark]`} /></label>
                    <label className="block"><span className="text-[10px] font-mono uppercase tracking-[0.2em] text-copper-400">Increase %</span>
                      <input value={sea.markup_percent || ""} onChange={(e) => update((c) => (c.settings.seasons[i].markup_percent = e.target.value))} placeholder="e.g. 30" className={inputCls} /></label>
                    <button onClick={() => update((c) => c.settings.seasons.splice(i, 1))} aria-label="Remove season" className="pb-2 text-cream-dim hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={() => update((c) => { c.settings.seasons = c.settings.seasons || []; c.settings.seasons.push({ name: "", from: "", to: "", markup_percent: "" }); })} className={addBtnCls} data-testid="admin-add-season-btn"><Plus className="h-3.5 w-3.5" /> Add season</button>
              </div>
              <Text label="Bank transfer details (optional)" textarea value={s.bank_details} onChange={(v) => update((c) => (c.settings.bank_details = v))} placeholder={"Account name\nAccount number\nIFSC\nBank & branch"} />
              <Text label="Payment note shown after booking" textarea rows={2} value={s.payment_note} onChange={(v) => update((c) => (c.settings.payment_note = v))} placeholder="e.g. Dates are held for 24 hours; booking is confirmed once the advance is received." />
            </div>
          </>
        )}

        {tab === "Hero" && (
          <>
            <Text label="Badge (location line)" value={content.hero.badge} onChange={(v) => update((c) => (c.hero.badge = v))} />
            <Text label="Headline line 1" testid="admin-hero-title-input" value={content.hero.title_lines[0]} onChange={(v) => update((c) => (c.hero.title_lines[0] = v))} />
            <Text label="Headline line 2 (copper italic)" value={content.hero.title_lines[1]} onChange={(v) => update((c) => (c.hero.title_lines[1] = v))} />
            <Text label="Subtitle" textarea value={content.hero.subtitle} onChange={(v) => update((c) => (c.hero.subtitle = v))} />
            <ImageField label="Hero image" testid="admin-hero-image-input" value={content.hero.image} onChange={(v) => update((c) => (c.hero.image = v))} />
            {(content.hero.trust || []).map((t, i) => (
              <Text key={i} label={`Trust indicator ${i + 1}`} value={t} onChange={(v) => update((c) => (c.hero.trust[i] = v))} />
            ))}
          </>
        )}

        {tab === "Villa" && (
          <>
            <Text label="Section heading" value={content.villa.heading} onChange={(v) => update((c) => (c.villa.heading = v))} />
            {(content.villa.chapters || []).map((ch, i) => (
              <Card key={i}>
                <Text label={`Chapter ${ch.num} title`} value={ch.title} onChange={(v) => update((c) => (c.villa.chapters[i].title = v))} />
                <Text label="Text" textarea value={ch.text} onChange={(v) => update((c) => (c.villa.chapters[i].text = v))} />
                <ImageField label="Image" testid={`admin-chapter-image-${i}`} value={ch.image} onChange={(v) => update((c) => (c.villa.chapters[i].image = v))} />
              </Card>
            ))}
          </>
        )}

        {tab === "Rooms" && (
          <>
            {(content.rooms || []).map((r, i) => (
              <Card key={r.id || i} onDelete={() => update((c) => c.rooms.splice(i, 1))}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Text label="Name" value={r.name} onChange={(v) => update((c) => (c.rooms[i].name = v))} />
                  <Text label="Tagline" value={r.tagline} onChange={(v) => update((c) => (c.rooms[i].tagline = v))} />
                </div>
                <Text label="Description" textarea value={r.description} onChange={(v) => update((c) => (c.rooms[i].description = v))} />
                <ImageField label="Image" testid={`admin-room-image-${i}`} value={r.image} onChange={(v) => update((c) => (c.rooms[i].image = v))} />
                <Text label="Specs (one per line: Label: Value)" textarea rows={4}
                  value={(r.specs || []).map((sp) => `${sp.label}: ${sp.value}`).join("\n")}
                  onChange={(v) => update((c) => (c.rooms[i].specs = v.split("\n").filter(Boolean).map((line) => { const [label, ...rest] = line.split(":"); return { label: label.trim(), value: rest.join(":").trim() }; })))} />
                <Text label="Price note" value={r.price_note} onChange={(v) => update((c) => (c.rooms[i].price_note = v))} />
                <label className="flex items-center gap-2 text-sm text-cream-muted">
                  <input type="checkbox" checked={!!r.primary} onChange={(e) => update((c) => (c.rooms[i].primary = e.target.checked))} className="accent-copper" />
                  Primary unit (large card)
                </label>
              </Card>
            ))}
            <button onClick={() => update((c) => c.rooms.push({ id: `room-${Date.now()}`, name: "", tagline: "", description: "", image: "", specs: [], price_note: "", primary: false }))} className={addBtnCls} data-testid="admin-add-room-btn"><Plus className="h-3.5 w-3.5" /> Add room</button>
          </>
        )}

        {tab === "Gallery" && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {(content.gallery || []).map((g, i) => (
                <Card key={g.id || i} onDelete={() => update((c) => c.gallery.splice(i, 1))}>
                  <ImageField label="Image" testid={`admin-gallery-image-${i}`} value={g.url} onChange={(v) => update((c) => (c.gallery[i].url = v))} />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Text label="Category" value={g.category} onChange={(v) => update((c) => (c.gallery[i].category = v))} placeholder="Exterior / Interiors / Around Karwar" />
                    <Text label="Alt text" value={g.alt} onChange={(v) => update((c) => (c.gallery[i].alt = v))} />
                  </div>
                </Card>
              ))}
            </div>
            <button onClick={() => update((c) => c.gallery.push({ id: `g-${Date.now()}`, url: "", category: "Interiors", alt: "" }))} className={addBtnCls} data-testid="admin-add-gallery-btn"><Plus className="h-3.5 w-3.5" /> Add photo</button>
          </>
        )}

        {tab === "Experiences" && (
          <>
            {(content.experiences || []).map((ex, i) => (
              <Card key={ex.id || i} onDelete={() => update((c) => c.experiences.splice(i, 1))}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Text label="Title" value={ex.title} onChange={(v) => update((c) => (c.experiences[i].title = v))} />
                  <Text label="Distance (hidden if empty)" value={ex.distance} onChange={(v) => update((c) => (c.experiences[i].distance = v))} placeholder="e.g. 2.5 km" />
                </div>
                <Text label="Description" textarea value={ex.description} onChange={(v) => update((c) => (c.experiences[i].description = v))} />
                <ImageField label="Image" testid={`admin-experience-image-${i}`} value={ex.image} onChange={(v) => update((c) => (c.experiences[i].image = v))} />
              </Card>
            ))}
            <button onClick={() => update((c) => c.experiences.push({ id: `e-${Date.now()}`, title: "", description: "", image: "", distance: "" }))} className={addBtnCls} data-testid="admin-add-experience-btn"><Plus className="h-3.5 w-3.5" /> Add experience</button>
          </>
        )}

        {tab === "FAQ" && (
          <>
            {(content.faq || []).map((f, i) => (
              <Card key={f.id || i} onDelete={() => update((c) => c.faq.splice(i, 1))}>
                <Text label="Question" value={f.q} onChange={(v) => update((c) => (c.faq[i].q = v))} />
                <Text label="Answer" textarea value={f.a} onChange={(v) => update((c) => (c.faq[i].a = v))} />
              </Card>
            ))}
            <button onClick={() => update((c) => c.faq.push({ id: `f-${Date.now()}`, q: "", a: "" }))} className={addBtnCls} data-testid="admin-add-faq-btn"><Plus className="h-3.5 w-3.5" /> Add FAQ</button>
          </>
        )}

        {tab === "Reviews" && (
          <>
            <p className="text-sm text-cream-muted">Leave rating/count empty to hide them until verified. Reviews you add here should be real guest quotes.</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Text label="Rating (e.g. 4.9)" value={content.reviews.rating} onChange={(v) => update((c) => (c.reviews.rating = v))} />
              <Text label="Review count (e.g. 87)" value={content.reviews.count} onChange={(v) => update((c) => (c.reviews.count = v))} />
            </div>
            <Text label="Note under reviews" value={content.reviews.note} onChange={(v) => update((c) => (c.reviews.note = v))} />
            {(content.reviews.items || []).map((r, i) => (
              <Card key={i} onDelete={() => update((c) => c.reviews.items.splice(i, 1))}>
                <Text label="Guest name" value={r.name} onChange={(v) => update((c) => (c.reviews.items[i].name = v))} />
                <Text label="Quote" textarea value={r.text} onChange={(v) => update((c) => (c.reviews.items[i].text = v))} />
              </Card>
            ))}
            <button onClick={() => update((c) => { c.reviews.items = c.reviews.items || []; c.reviews.items.push({ name: "", text: "", stars: 5 }); })} className={addBtnCls} data-testid="admin-add-review-btn"><Plus className="h-3.5 w-3.5" /> Add review</button>
          </>
        )}

        {tab === "SEO" && (
          <>
            <Text label="Home meta title" value={content.seo.home_title} onChange={(v) => update((c) => (c.seo.home_title = v))} />
            <Text label="Home meta description" textarea value={content.seo.home_description} onChange={(v) => update((c) => (c.seo.home_description = v))} />
            <Text label="Blog meta title" value={content.seo.blog_title} onChange={(v) => update((c) => (c.seo.blog_title = v))} />
            <Text label="Blog meta description" textarea value={content.seo.blog_description} onChange={(v) => update((c) => (c.seo.blog_description = v))} />
            <ImageField label="Social share (OG) image" testid="admin-og-image-input" value={content.seo.og_image} onChange={(v) => update((c) => (c.seo.og_image = v))} />
            <p className="text-xs text-cream-dim">Sitemap: /api/sitemap.xml · Robots: /api/robots.txt (generated automatically)</p>
          </>
        )}

        {tab === "Bookings" && <BookingsTab />}

        {tab === "Calendar" && <CalendarTab />}

        {tab === "Blog" && (
          <>
            {!editing ? (
              <>
                <div className="space-y-2">
                  {posts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-charcoal-800/60 px-4 py-3" data-testid={`admin-post-row-${p.slug}`}>
                      <div>
                        <p className="text-sm text-cream">{p.title} {!p.published && <span className="text-xs text-cream-dim">(draft)</span>}</p>
                        <p className="text-xs text-cream-dim">/blog/{p.slug}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditing(p)} className="rounded-full border border-copper/40 px-3 py-1.5 text-xs text-copper-300 hover:bg-copper/10" data-testid={`admin-edit-post-${p.slug}`}>Edit</button>
                        <button onClick={async () => { await api.delete(`/admin/blog/${p.id}`); setPosts(posts.filter((x) => x.id !== p.id)); toast.success("Deleted"); }}
                          className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-cream-dim hover:text-red-400" data-testid={`admin-delete-post-${p.slug}`}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={() => setEditing({ title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: [], published: true })} className={addBtnCls} data-testid="admin-new-post-btn"><Plus className="h-3.5 w-3.5" /> New post</button>
              </>
            ) : (
              <Card>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Text label="Title" testid="admin-post-title-input" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v, slug: editing.slug || v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} />
                  <Text label="Slug" testid="admin-post-slug-input" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
                </div>
                <Text label="Excerpt" textarea value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} />
                <Text label="Content (blank line = new paragraph, '## ' = heading)" textarea rows={12} testid="admin-post-content-input" value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} />
                <ImageField label="Cover image" testid="admin-post-cover-input" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} />
                <Text label="Tags (comma separated)" value={(editing.tags || []).join(", ")} onChange={(v) => setEditing({ ...editing, tags: v.split(",").map((t) => t.trim()).filter(Boolean) })} />
                <label className="flex items-center gap-2 text-sm text-cream-muted">
                  <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-copper" data-testid="admin-post-published-toggle" />
                  Published
                </label>
                <div className="flex gap-3 pt-2">
                  <button onClick={savePost} className="rounded-full bg-copper px-6 py-2.5 text-xs font-mono uppercase tracking-[0.15em] text-charcoal hover:bg-copper-300" data-testid="admin-save-post-btn">Save post</button>
                  <button onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-6 py-2.5 text-xs text-cream-muted" data-testid="admin-cancel-post-btn">Cancel</button>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </main>
  );
}
